import { KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react'

import {
  FlxButton,
  FlxFormLabel,
  FlxFormLabelContainer,
  FlxInput,
  FlxInputContainer,
  FlxInputField,
  FlxTextArea,
  FlxTextAreaContainer,
} from '@flowx/react-ui-toolkit'

import {
  DESCRIPTION_MAX_LENGTH,
  EDGE_LABEL_MAX_LENGTH,
} from '@flowx/designer/shared/api/resource-description.api'

import { EdgeDescription } from './useEdgeDescription'

// A stored value that loads after the popover opened replaces the draft only while the user has not
// typed over it.
export const hydrateDraft = <T,>(draft: T, previousStored: T, stored: T): T =>
  draft === previousStored ? stored : draft

const descriptionPlaceholder = (loadFailed: boolean, draft: string | null): string => {
  if (loadFailed) {
    return "Couldn't load the description"
  }

  return draft === null ? 'Loading…' : 'Add a description'
}

export type EdgeDocumentationChanges = {
  label?: string
  description?: string
}

type EdgeDocumentationDraft = {
  label: string
  description: string | null
}

// Only the fields the user changed, trimmed; a change of spaces alone is no change.
export const edgeDocumentationChanges = (
  draft: EdgeDocumentationDraft,
  stored: EdgeDocumentationDraft
): EdgeDocumentationChanges => {
  const label = draft.label.trim()
  const description = draft.description?.trim() ?? null

  return {
    ...(label !== stored.label.trim() ? { label } : {}),
    ...(description !== null && description !== (stored.description?.trim() ?? null)
      ? { description }
      : {}),
  }
}

export type EdgeDocumentationPopoverProps = {
  label: string
  edge: EdgeDescription
  readOnly: boolean
  onCommit: (changes: EdgeDocumentationChanges) => void
  onClose: () => void
}

export const EdgeDocumentationPopover = ({
  label,
  edge,
  readOnly,
  onCommit,
  onClose,
}: EdgeDocumentationPopoverProps): ReactElement => {
  const [labelDraft, setLabelDraft] = useState(label)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(edge.description)
  const [drafting, setDrafting] = useState(false)
  const [draftFailed, setDraftFailed] = useState(false)
  const latest = useRef({ label: labelDraft, description: descriptionDraft })
  const initial = useRef({ label, description: edge.description })
  const commit = useRef(onCommit)

  latest.current = { label: labelDraft, description: descriptionDraft }
  commit.current = onCommit

  // Labels can still be loading when the edge is selected.
  useEffect(() => {
    const previous = initial.current.label
    initial.current = { ...initial.current, label }
    setLabelDraft((draft) => hydrateDraft(draft, previous, label))
  }, [label])

  // The stored description arrives after the popover opens.
  useEffect(() => {
    const previous = initial.current.description
    initial.current = { ...initial.current, description: edge.description }
    setDescriptionDraft((draft) => hydrateDraft(draft, previous, edge.description))
  }, [edge.description])

  useEffect(
    () => () => {
      if (readOnly) {
        return
      }

      const changes = edgeDocumentationChanges(latest.current, initial.current)

      if (Object.keys(changes).length) {
        commit.current(changes)
      }
    },
    [readOnly]
  )

  const generate = (): void => {
    if (!edge.draft) {
      return
    }

    setDrafting(true)
    setDraftFailed(false)
    edge.draft().subscribe({
      next: (draft) => {
        setDrafting(false)
        if (draft.label != null) {
          setLabelDraft(draft.label.slice(0, EDGE_LABEL_MAX_LENGTH))
        }
        // Without the stored text the user cannot see what a draft would replace.
        if (draft.description != null && !edge.loadFailed) {
          setDescriptionDraft(draft.description)
        }
      },
      error: () => {
        setDrafting(false)
        setDraftFailed(true)
      },
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()

      return
    }

    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }

  return (
    <div
      className="nodrag nopan nowheel w-[300px] rounded-xl border border-neutral-200 bg-white p-3 shadow-lg"
      data-testid="edge-documentation-popover"
      onKeyDown={onKeyDown}
      // The card is portalled, but React events still bubble to the edge and would reselect it.
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <FlxInputContainer className="flx-ds w-full">
        <FlxFormLabelContainer className="mb-1.5 flex items-center justify-between">
          <FlxFormLabel className="flx-ds">Label</FlxFormLabel>
          {edge.draft && (
            <FlxButton
              onClick={generate}
              disabled={drafting}
              icon="center"
              className="flx-ds secondary small"
              iconComponent={
                <i
                  className={
                    drafting ? 'ph ph-spinner-gap animate-spin' : 'ph ph-sparkle text-yellow-500'
                  }
                />
              }
              title="Generate"
              data-testid="edge-documentation-generate"
            />
          )}
        </FlxFormLabelContainer>
        <FlxInput>
          <FlxInputField
            className="flx-ds"
            value={labelDraft}
            disabled={readOnly}
            maxLength={EDGE_LABEL_MAX_LENGTH}
            placeholder="Name this branch"
            onChange={(event) => setLabelDraft(event.target.value)}
            data-testid="edge-label-input"
          />
        </FlxInput>
      </FlxInputContainer>

      <FlxTextAreaContainer className="flx-ds mt-2 w-full [&_textarea]:text-sm [&_textarea]:leading-[22px]">
        <FlxFormLabelContainer className="mb-1.5">
          <FlxFormLabel className="flx-ds">Description</FlxFormLabel>
        </FlxFormLabelContainer>
        <FlxTextArea
          style={{ width: '100%' }}
          value={descriptionDraft ?? ''}
          disabled={readOnly || descriptionDraft === null}
          maxLength={DESCRIPTION_MAX_LENGTH}
          placeholder={descriptionPlaceholder(edge.loadFailed, descriptionDraft)}
          heightType="auto"
          minRows={2}
          maxRows={4}
          onChange={(event) => setDescriptionDraft(event.target.value)}
          data-testid="edge-description-input"
        />
      </FlxTextAreaContainer>

      {edge.loadFailed && (
        <div
          className="mt-2 flex items-center justify-between gap-2 text-xs text-red-500"
          data-testid="edge-description-load-error"
        >
          <span>Couldn't load the description.</span>
          <FlxButton
            onClick={edge.reload}
            className="flx-ds ternary small"
            data-testid="edge-description-retry"
          >
            Retry
          </FlxButton>
        </div>
      )}

      {draftFailed && (
        <p
          className="mt-2 text-xs text-red-500"
          data-testid="edge-documentation-generate-error"
        >
          Couldn't generate. Try again.
        </p>
      )}
    </div>
  )
}
